import useInsuranceSplit from 'app/ProviderDetails/InsuranceSection/useInsuranceSplit'
import { AvailabilityFilterValues } from 'app/ProviderSearch/constants'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import useAppSelector from 'hooks/useAppSelector'
import { useMemo } from 'react'
import { getFormattedPracticeAddress } from 'utils/common'

type ProviderListingDetailsReturnType = {
  availabilityTags: string[]
  practiceLocation: string
  areaOfConcernValues: string[]
  insuranceNamesValues: string[]
}

const useProviderListingDetails = (
  providerDetails: Provider,
  isSupportUser?: boolean
): ProviderListingDetailsReturnType => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]
  const areaOfConcernFilters =
    globalGroupedFilters[ProviderFilterTypes.AREA_OF_CONCERN]
  const { availabilityTypeIds, areaOfConcernIds, insuranceNames, practice } =
    providerDetails ?? {}

  const practiceLocation = isSupportUser
    ? getFormattedPracticeAddress(practice).address
    : `${practice.city}, ${practice.state}`

  // As per https://thriveworks.atlassian.net/browse/THRIV-3528
  // Hiding the intro text, but it could come in later tickets
  // const introText = getTextFromHtml(providerSummary || shortIntro || '')

  //------------------------------------ Insurance section data handling
  const [topInsurances, otherInsurances] = useInsuranceSplit(
    insuranceNames?.map((insuranceName) => ({
      insuranceProvider: {
        name: insuranceName,
      },
    }))
  )

  let insuranceNamesValues = [...topInsurances, ...otherInsurances].map(
    (insurance) => insurance.insuranceProvider.name
  )

  if (insuranceNamesValues?.length > 4) {
    const startArray = insuranceNamesValues.slice(0, 4)
    startArray.push(`+${insuranceNamesValues.length - 4} more`)

    insuranceNamesValues = startArray
  }
  //----------------------------------------------------------

  const availabilityTags: string[] = useMemo(
    () =>
      availabilityTypeIds?.reduce((accumulator, availabilityId) => {
        const availabilityFilter = availabilityTypeFilters.find(
          (availabilityFilter) => availabilityFilter.id === availabilityId
        )

        if (availabilityFilter?.value === AvailabilityFilterValues.inPerson) {
          accumulator.push('in-person')
        } else if (
          availabilityFilter?.value === AvailabilityFilterValues.online
        ) {
          accumulator.push('video')
        }

        return accumulator
      }, []) ?? [],
    [availabilityTypeIds, availabilityTypeFilters]
  )

  const areaOfConcernValues = useMemo(() => {
    const areaOfConcernvalues = areaOfConcernIds?.reduce(
      (accumulator, areaOfConcernId) => {
        const availabilityFilter = areaOfConcernFilters.find(
          (areaOfConcernFilter) => areaOfConcernFilter.id === areaOfConcernId
        )

        if (availabilityFilter) {
          accumulator.push(availabilityFilter.value)
        }

        return accumulator
      },
      []
    )

    // This will give a array that displays 4 items and rest are calculated to be added in +N more
    // LGBTQIA+, Anxiety, Autism, Addiction +3 more
    if (areaOfConcernvalues?.length > 4) {
      const startArray = areaOfConcernvalues.slice(0, 4)
      startArray.push(`+${areaOfConcernvalues.length - 4} more`)

      return startArray
    }

    return areaOfConcernvalues ?? []
  }, [areaOfConcernFilters, areaOfConcernIds])

  return {
    availabilityTags,
    practiceLocation,
    areaOfConcernValues,
    insuranceNamesValues,
  }
}

export default useProviderListingDetails
